import React, { useEffect, useRef, useState } from "react";
import { MapContainer, TileLayer, Polygon, Marker, Popup, FeatureGroup, useMapEvents } from "react-leaflet";
import L from "leaflet";
import { api } from "../lib/api";
import { Card, PageHeader, Btn, Input, Select, Pill } from "../components/UI";
import { Pencil, Trash2, Save, X, MapPin } from "lucide-react";

// Fix default marker icons in Leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
});

const TYPE_COLORS = {
  advertiser: "var(--accent-primary)",
  competitor: "var(--danger)",
  event: "var(--plum)",
  exclusion: "var(--text-muted)",
  conversion: "var(--success)",
  suppression: "var(--accent-deep)",
};

function DrawController({ drawing, onPoint }) {
  useMapEvents({
    click(e) {
      if (drawing) onPoint([e.latlng.lat, e.latlng.lng]);
    },
  });
  return null;
}

export default function GeoIntelligence() {
  const [polygons, setPolygons] = useState([]);
  const [competitors, setCompetitors] = useState([]);
  const [drawing, setDrawing] = useState(false);
  const [currentPts, setCurrentPts] = useState([]);
  const [form, setForm] = useState({ name: "", type: "advertiser", notes: "" });
  const [center] = useState([33.1507, -96.8236]); // Frisco, TX
  const mapRef = useRef(null);

  const load = async () => {
    const [p, c] = await Promise.all([api.get("/polygons"), api.get("/competitors")]);
    setPolygons(p.data); setCompetitors(c.data);
  };
  useEffect(() => { load(); }, []);

  const startDraw = () => { setDrawing(true); setCurrentPts([]); };
  const cancelDraw = () => { setDrawing(false); setCurrentPts([]); };
  const undoLast = () => setCurrentPts(pts => pts.slice(0, -1));

  const savePolygon = async () => {
    if (currentPts.length < 3) { alert("Polygon needs at least 3 points"); return; }
    if (!form.name) { alert("Name required"); return; }
    await api.post("/polygons", {
      name: form.name,
      type: form.type,
      coordinates: currentPts,
      notes: form.notes,
      color: TYPE_COLORS[form.type] || "var(--accent-primary)",
    });
    setDrawing(false); setCurrentPts([]);
    setForm({ name: "", type: "advertiser", notes: "" });
    load();
  };

  const deletePolygon = async (id) => {
    if (!window.confirm("Delete polygon?")) return;
    await api.delete(`/polygons/${id}`); load();
  };

  return (
    <div className="p-6 lg:p-8 space-y-6">
      <PageHeader
        eyebrow="Polygon Geo-Targeting"
        title="Geo Intelligence"
        subtitle="Draw precision polygons, conversion zones, exclusion areas, and competitor boundaries."
        action={
          drawing ? (
            <div className="flex gap-2">
              <Btn variant="secondary" onClick={undoLast}>Undo</Btn>
              <Btn variant="secondary" onClick={cancelDraw}><X className="w-4 h-4" /> Cancel</Btn>
              <Btn onClick={savePolygon} data-testid="save-polygon-btn"><Save className="w-4 h-4" /> Save polygon ({currentPts.length})</Btn>
            </div>
          ) : (
            <Btn onClick={startDraw} data-testid="draw-polygon-btn"><Pencil className="w-4 h-4" /> Draw polygon</Btn>
          )
        }
      />

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
        <div className="lg:col-span-3">
          <Card padded={false} className="overflow-hidden">
            <div className="h-[560px] relative">
              <MapContainer center={center} zoom={12} style={{ height: "100%", width: "100%" }} ref={mapRef} data-testid="leaflet-map">
                <TileLayer
                  url="https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png"
                  attribution='&copy; OpenStreetMap &copy; CARTO'
                />
                <DrawController drawing={drawing} onPoint={(p) => setCurrentPts(pts => [...pts, p])} />
                {polygons.map(p => (
                  <Polygon key={p.id} positions={p.coordinates} pathOptions={{ color: p.color || TYPE_COLORS[p.type] || "var(--accent-primary)", weight: 2, fillOpacity: 0.2 }}>
                    <Popup>
                      <div className="text-slate-200">
                        <strong>{p.name}</strong><br />
                        <span className="text-xs">{p.type}</span>
                      </div>
                    </Popup>
                  </Polygon>
                ))}
                {currentPts.length >= 3 && (
                  <Polygon positions={currentPts} pathOptions={{ color: TYPE_COLORS[form.type], weight: 2, dashArray: "4 4", fillOpacity: 0.3 }} />
                )}
                {currentPts.map((p, i) => (
                  <Marker key={i} position={p} />
                ))}
                {competitors.filter(c => c.latitude && c.longitude).map(c => (
                  <Marker key={c.id} position={[c.latitude, c.longitude]}>
                    <Popup>
                      <strong>{c.business_name}</strong><br />
                      <span className="text-xs">{c.category}</span>
                    </Popup>
                  </Marker>
                ))}
              </MapContainer>

              {drawing && (
                <div className="absolute top-4 left-4 z-[1000] glass-card rounded-md p-3 max-w-xs" data-testid="draw-form">
                  <div className="text-xs uppercase tracking-wider text-cyan-300 font-medium mb-2">Drawing polygon · click to add points</div>
                  <Input label="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mb-2" data-testid="polygon-name" />
                  <Select label="Type" value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })}>
                    {Object.keys(TYPE_COLORS).map(t => <option key={t}>{t}</option>)}
                  </Select>
                </div>
              )}
            </div>
          </Card>
        </div>

        <div className="space-y-3">
          <Card>
            <div className="text-xs uppercase tracking-wider text-slate-400 font-medium mb-2">Polygon types</div>
            <div className="space-y-1.5 text-sm">
              {Object.entries(TYPE_COLORS).map(([t, c]) => (
                <div key={t} className="flex items-center gap-2 text-slate-300">
                  <span className="w-3 h-3 rounded-sm" style={{ background: c, opacity: 0.7 }} />
                  <span className="capitalize">{t}</span>
                </div>
              ))}
            </div>
          </Card>

          <Card padded={false} data-testid="tracked-locations-card">
            <div className="px-5 py-3 border-b border-white/10">
              <div className="text-xs uppercase tracking-wider text-slate-400 font-medium">Tracked locations</div>
              <div className="text-xs text-slate-500 mt-0.5">{competitors.length} geofenced targets</div>
            </div>
            <div className="max-h-[260px] overflow-y-auto divide-y divide-white/5" data-testid="tracked-locations-list">
              {competitors.map(c => (
                <div key={c.id} className="px-5 py-3" data-testid={`tracked-location-${c.id}`}>
                  <div className="text-sm text-white truncate">{c.business_name}</div>
                  <div className="text-[10px] text-slate-500">{c.category}{c.address ? ` · ${c.address}` : ""}</div>
                </div>
              ))}
              {competitors.length === 0 && <div className="px-5 py-4 text-xs text-slate-500">No tracked locations yet.</div>}
            </div>
          </Card>

          <Card padded={false}>
            <div className="px-5 py-3 border-b border-white/10">
              <div className="text-xs uppercase tracking-wider text-slate-400 font-medium">Saved polygons</div>
              <div className="text-xs text-slate-500 mt-0.5">{polygons.length} total</div>
            </div>
            <div className="max-h-[420px] overflow-y-auto divide-y divide-white/5" data-testid="polygons-list">
              {polygons.map(p => (
                <div key={p.id} className="px-5 py-3 flex items-center justify-between gap-2">
                  <div className="min-w-0">
                    <div className="text-sm text-white truncate">{p.name}</div>
                    <div className="text-[10px] text-slate-500 capitalize">{p.type} · {p.coordinates.length} pts</div>
                  </div>
                  <button onClick={() => deletePolygon(p.id)} className="text-slate-500 hover:text-rose-300" data-testid={`delete-polygon-${p.id}`}>
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
              {polygons.length === 0 && <div className="px-5 py-6 text-center text-xs text-slate-500">Draw your first polygon</div>}
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}
